import { useQuery } from '@tanstack/react-query';

import { ImpersonationBroker, type IPagedCollection } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { IFormFieldOption } from '../../viewModels/form';

/**
 * One option (choice) for an `Option` / `MultipleOptions` deal-form
 * field. Mirrors what `DealField` actually renders inside its Select /
 * Combobox dropdowns: a stable id and a human-readable label.
 */
export type DealFieldOption = {
	id: string;
	label: string;
};

export type UseDealFieldOptionsResult = {
	options: DealFieldOption[];
	isLoading: boolean;
	error: Error | null;
};

/**
 * Lazy-loads the choices for a single `Option` / `MultipleOptions` deal
 * form field.
 *
 * Mirrors the legacy `FormFieldViewModel.loadOptions`
 * (`aida/viewModels/form.ts:312-336`) which fetches from
 * `${baseApiUrl}/Field/${field.id}` via a paginated controller. The deal
 * form's `baseApiUrl` is `lead/{companyId}/CreateDeal`, so the endpoint
 * is `lead/{companyId}/CreateDeal/Field/{fieldId}`.
 *
 * The legacy endpoint is paginated, but in practice every option list
 * fits on a single page (the largest production list we've seen — Deal
 * Vertical — is well under the default 25-item page size). We fetch the
 * first page only; if the dropdown ever needs to surface more, the
 * follow-up is a `useInfiniteQuery` that wires `pageToken` through.
 *
 * Disabled when either id is missing — `DealField` only mounts this for
 * `Option` / `MultipleOptions` types when no inline options were
 * supplied.
 */
export function useDealFieldOptions(
	companyId: string | undefined,
	fieldId: string | undefined
): UseDealFieldOptionsResult {
	const userSession = useUserSession();

	const query = useQuery<DealFieldOption[], Error>({
		queryKey: ['focused-view', 'deal-form', 'field-options', companyId, fieldId] as const,
		enabled: !!companyId && !!fieldId,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callAsync<IPagedCollection<IFormFieldOption>>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${companyId}/CreateDeal/Field/${fieldId}`,
				}),
				'GET'
			);
			return (response.values ?? []).map(o => ({ id: o.id ?? '', label: o.label ?? '' }));
		},
	});

	return {
		options: query.data ?? [],
		isLoading: query.isLoading,
		error: query.error ?? null,
	};
}
